import React, { useEffect, useState } from "react";
import {
  useAccount,
  useWriteContract,
  useWaitForTransactionReceipt
} from "wagmi";
import { useCoinageContract } from "../../../../useCoinageContract";
import { useUserContext } from "../../../../contexts/User";
import { useWizard } from "react-use-wizard";
import { networkMint } from "../../../../utils/api/mint";
import { StepContent } from "../Components";
import { Button } from "../../../Actions";
import { useTransactionUrl } from "../../../../utils/buildTransactionUrl";
import { extractErrorData } from "../../../../utils/extractError";
import NFTPill from "../../../NFTPill/NFTPill";
import MintError from "../../../MintError/MintError";
import { WrapMintButtonWithChangeChain } from "../../../SwitchNetworkButton";
import { parseEther } from "viem";

const NetworkMint: React.FC = () => {
  const [status, setStatus] = useState("");
  const [error, setError] = useState<{ message: string; call?: string }>({
    message: "",
    call: undefined
  });
  const { user } = useUserContext();
  const { address, isDisconnected } = useAccount();
  const { nextStep } = useWizard();

  const {
    writeContract,
    data: mintData,
    isPending
  } = useWriteContract({
    mutation: {
      onSettled(data, error) {
        if (error) {
          setStatus("error");
          setError(extractErrorData(error));
        }
      }
    }
  });

  const contractDetails = useCoinageContract();

  const { data: transactionReceipt, status: transactionStatus } =
    useWaitForTransactionReceipt({
      hash: mintData,
      chainId: 1
    });

  useEffect(() => {
    if (transactionStatus === "success" && transactionReceipt) {
      nextStep();
    }
  }, [transactionStatus, transactionReceipt, nextStep]);

  const transactionUrl = useTransactionUrl(mintData);

  const handleMint = async () => {
    if (!user?.referralCode) return;
    setStatus("loading");
    const req = {
      referral: user.referralCode,
      address: address as string
    };
    const { data, error } = await networkMint(req);

    if (error) {
      setStatus("error");
    }
    const { hash, signature, networkGroupId } = data;

    const cost = parseEther("1.1");

    try {
      await writeContract({
        ...contractDetails,
        functionName: "networkMint",
        args: [hash, signature, user?.referralCode, networkGroupId, user.id],
        value: cost
      });
    } catch (err) {
      console.error(JSON.stringify(err));
      setStatus("error");
      const errorData = extractErrorData(err);

      setError({
        message: errorData.message
          ? errorData.message
          : "sorry something went wrong",
        call: "premint"
      });
    }
  };

  return (
    <StepContent title="Mint Network Membership Pass.">
      <NFTPill
        video="/network/network600.mp4"
        title="Network Membership Pass"
        price={{ label: 1 }}
      />
      <WrapMintButtonWithChangeChain>
        <Button
          modifier="primary"
          analyticsProperty="networkMint"
          disabled={isDisconnected}
          loading={isPending || status === "loading"}
          onClick={handleMint}>
          Mint
        </Button>
      </WrapMintButtonWithChangeChain>
      <MintError
        mintData={mintData}
        transactionUrl={transactionUrl}
        error={error}
        show={status === "error"}
      />
    </StepContent>
  );
};

export default NetworkMint;
